import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Download, Plus, Printer, ReceiptText, Trash2 } from 'lucide-react';
import { Button, DataTable, FormField, Modal, PageHeader, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import { listAttachements, listChantiers } from '@/services/chantiers';
import {
  type SituationDTO,
  type SituationInput,
  type SituationStatut,
  changerStatutSituation,
  createSituation,
  deleteSituation,
  genererFactureDepuisSituation,
  listSituations,
  openSituationPdf,
  updateSituation,
} from '@/services/situations';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const STATUT_UI: Record<SituationStatut, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  SOUMIS: { label: 'Soumis', status: 'info' },
  VALIDE: { label: 'Validé', status: 'done' },
  REJETE: { label: 'Rejeté', status: 'stuck' },
  PAYE: { label: 'Payé', status: 'review' },
};

interface FormValues {
  chantier_id: string;
  numero: number;
  mois: number;
  annee: number;
  taux_revision: number;
  retenue_garantie_pct: number;
  avance_a_rembourser: number;
  lignes: { designation: string; unite: string; quantite_periode: number; prix_unitaire: number }[];
}

const SITUATION_STATUTS: SituationStatut[] = ['BROUILLON', 'SOUMIS', 'VALIDE', 'REJETE', 'PAYE'];

export function SituationsPage() {
  const perms = usePerms('situations');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['situations', { showArchived }], queryFn: () => listSituations({ archives: showArchived }) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: modalOpen });

  const invalidateS = () => { queryClient.invalidateQueries({ queryKey: ['situations'] }); queryClient.invalidateQueries({ queryKey: ['notifications'] }); };
  const statutMutation = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: SituationStatut }) => changerStatutSituation(id, statut),
    onSuccess: () => invalidateS(),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  // Génération assistée : brouillon de facture Production lié, ouvert pour validation manuelle.
  const genererMutation = useMutation({
    mutationFn: (id: string) => genererFactureDepuisSituation(id),
    onSuccess: (f) => {
      queryClient.invalidateQueries({ queryKey: ['factures-vente'] });
      toast.success(`Brouillon de facture généré (HT ${formatDZD(f.montant_ht)}). À valider dans Factures → Production.`);
      navigate('/factures?tab=production');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateSituation(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidateS(); toast.success(v.archived ? 'Situation archivée.' : 'Situation restaurée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSituation(id),
    onSuccess: () => { invalidateS(); toast.success('Situation supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const now = new Date();
  const { register, handleSubmit, control, reset, watch } = useForm<FormValues>({
    defaultValues: {
      numero: 1,
      mois: now.getMonth() + 1,
      annee: now.getFullYear(),
      taux_revision: 0,
      retenue_garantie_pct: 5,
      avance_a_rembourser: 0,
      lignes: [{ designation: '', unite: '', quantite_periode: 0, prix_unitaire: 0 }],
    },
  });
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'lignes' });

  // Flux §4 : importer un attachement (terrain, quantités) pour pré-remplir les
  // lignes du décompte ; les prix unitaires restent à valoriser ici (finance).
  const selectedChantier = watch('chantier_id');
  const { data: attachements } = useQuery({
    queryKey: ['attachements', selectedChantier],
    queryFn: () => listAttachements(selectedChantier),
    enabled: modalOpen && !!selectedChantier,
  });
  const importerAttachement = (attId: string) => {
    const att = attachements?.find((a) => a.id === attId);
    if (!att) return;
    replace(
      att.lignes.length
        ? att.lignes.map((l) => ({ designation: l.designation, unite: l.unite ?? '', quantite_periode: Number(l.quantite), prix_unitaire: 0 }))
        : [{ designation: '', unite: '', quantite_periode: 0, prix_unitaire: 0 }],
    );
    toast.success(`Attachement N° ${att.numero} importé — complétez les prix unitaires.`);
  };

  const createMutation = useMutation({
    mutationFn: (v: FormValues) => {
      const payload: SituationInput = {
        chantier_id: v.chantier_id,
        numero: Number(v.numero),
        mois: Number(v.mois),
        annee: Number(v.annee),
        taux_revision: Number(v.taux_revision) / 100,
        retenue_garantie_pct: Number(v.retenue_garantie_pct),
        avance_a_rembourser: Number(v.avance_a_rembourser),
        lignes: v.lignes.map((l) => ({
          designation: l.designation,
          unite: l.unite || null,
          quantite_periode: Number(l.quantite_periode),
          prix_unitaire: Number(l.prix_unitaire),
        })),
      };
      return createSituation(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['situations'] });
      setModalOpen(false);
      reset();
      toast.success('Situation créée.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const handlePdf = async (id: string, mode: 'open' | 'download' = 'open') => {
    try {
      await openSituationPdf(id, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const columns: ColumnDef<SituationDTO, unknown>[] = [
    { accessorKey: 'numero', header: 'N°', size: 70, cell: ({ getValue }) => `N° ${getValue()}` },
    {
      id: 'periode',
      header: 'Période',
      accessorFn: (s) => `${String(s.mois).padStart(2, '0')}/${s.annee}`,
      size: 110,
    },
    {
      accessorKey: 'montant_travaux_revise',
      header: 'Montant révisé',
      cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span>,
    },
    {
      accessorKey: 'net_a_payer',
      header: 'Net à payer',
      cell: ({ getValue }) => <span className="font-semibold tabular-nums text-brand-primary">{formatDZD(getValue() as number)}</span>,
    },
    {
      accessorKey: 'statut',
      header: 'Statut',
      size: 130,
      cell: ({ row }) =>
        showArchived ? (
          <StatusBadge status={STATUT_UI[row.original.statut].status} label={STATUT_UI[row.original.statut].label} />
        ) : (
          <select
            value={row.original.statut}
            onChange={(e) => statutMutation.mutate({ id: row.original.id, statut: e.target.value as SituationStatut })}
            className="h-8 rounded-md border border-border bg-surface-secondary px-2 text-xs font-semibold"
          >
            {SITUATION_STATUTS.map((s) => <option key={s} value={s}>{STATUT_UI[s].label}</option>)}
          </select>
        ),
    },
    {
      id: 'actions',
      header: '',
      size: 160,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          {(row.original.statut === 'VALIDE' || row.original.statut === 'PAYE') && perms.canAjouter && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-brand-primary" loading={genererMutation.isPending}
              onClick={() => genererMutation.mutate(row.original.id)} aria-label="Générer la facture client" title="Générer la facture client">
              <ReceiptText className="size-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePdf(row.original.id, 'open')} aria-label="Imprimer la situation"><Printer className="size-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePdf(row.original.id, 'download')} aria-label="Télécharger la situation"><Download className="size-4" /></Button>
          {showArchived ? (
            perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: false })} aria-label="Restaurer"><ArchiveRestore className="size-4" /></Button>
          ) : (
            perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: true })} aria-label="Archiver"><Archive className="size-4" /></Button>
          )}
          {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer la situation N° ${row.original.numero} ?`)) deleteMutation.mutate(row.original.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Situations de travaux"
        subtitle="Décomptes mensuels et génération des PDF"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Situations de travaux' }]}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Archivées
            </label>
            {perms.canAjouter && (
              <Button onClick={() => setModalOpen(true)}>
                <Plus /> Nouvelle situation
              </Button>
            )}
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pageSize={15}
        exportFileName="situations"
        emptyMessage="Aucune situation de travaux."
      />

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title="Nouvelle situation de travaux"
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>
              Créer la situation
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FormField label="Chantier" required className="col-span-2 sm:col-span-4">
            <select {...register('chantier_id')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">— Sélectionner —</option>
              {chantiers?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.nom}
                </option>
              ))}
            </select>
          </FormField>
          {selectedChantier && (attachements?.length ?? 0) > 0 && (
            <FormField label="Importer un attachement (quantités terrain — prix à compléter)" className="col-span-2 sm:col-span-4">
              <select
                defaultValue=""
                onChange={(e) => { if (e.target.value) importerAttachement(e.target.value); e.target.value = ''; }}
                className="flex h-10 w-full rounded-md border border-dashed border-brand-accent bg-surface-secondary px-3 text-sm"
              >
                <option value="">— Choisir un attachement à valoriser —</option>
                {attachements?.map((a) => (
                  <option key={a.id} value={a.id}>
                    Attachement N° {a.numero}{a.objet ? ` · ${a.objet}` : ''} ({a.lignes.length} ligne{a.lignes.length > 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="N°">
            <Input type="number" {...register('numero')} />
          </FormField>
          <FormField label="Mois">
            <Input type="number" min="1" max="12" {...register('mois')} />
          </FormField>
          <FormField label="Année">
            <Input type="number" {...register('annee')} />
          </FormField>
          <FormField label="Révision (%)">
            <Input type="number" step="any" {...register('taux_revision')} />
          </FormField>
          <FormField label="Retenue garantie (%)">
            <Input type="number" step="any" {...register('retenue_garantie_pct')} />
          </FormField>
          <FormField label="Avance à rembourser (DZD)" className="sm:col-span-3">
            <Input type="number" step="1" {...register('avance_a_rembourser')} />
          </FormField>
        </div>

        {/* Lignes du décompte */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Lignes du décompte</p>
            <Button variant="ghost" size="sm" onClick={() => append({ designation: '', unite: '', quantite_periode: 0, prix_unitaire: 0 })}>
              <Plus className="size-4" /> Ligne
            </Button>
          </div>
          <div className="space-y-2">
            {fields.map((field, i) => (
              <div key={field.id} className="grid grid-cols-12 gap-2">
                <Input placeholder="Désignation" className="col-span-5" {...register(`lignes.${i}.designation`)} />
                <Input placeholder="Unité" className="col-span-2" {...register(`lignes.${i}.unite`)} />
                <Input type="number" step="0.001" placeholder="Qté" className="col-span-2" {...register(`lignes.${i}.quantite_periode`)} />
                <Input type="number" step="1" placeholder="P.U." className="col-span-2" {...register(`lignes.${i}.prix_unitaire`)} />
                <Button
                  variant="ghost"
                  size="icon"
                  className="col-span-1 h-10 text-status-stuck"
                  onClick={() => remove(i)}
                  disabled={fields.length === 1}
                  aria-label="Retirer la ligne"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}
